import React, { useRef } from 'react';
import PageScrollTable, { useEditingRowDim } from '../ui/PageScrollTable';
import Tabs from '../ui/Tabs';
import Pagination from '../Pagination';
import { ExpandChevron, ExpandedPanelRow, EXPANDED_ROW_FRAME } from '../TableRowExpansion';
import PoOrderTopLine from '../PoOrderTopLine';
import PhonePoOrderCell from '../PhonePoOrderCell';
import { tightenPhoneColumns } from '../../constants/phoneLayout';
import { statusPillClass } from '../ui/StatusPill';
import { OrderHeaderNoteContext } from '../OrderSectionHeader';
import {
  expandColumn, poOrderColumn, requesterColumn, supplierColumn, statusColumn,
  PHONE_PO_ORDER_COLUMN,
} from '../sharedTableColumns';
import {
  compareBlanksLastText, compareRanked, hireSortSign, isNullishBlank, oneBlankLast,
} from '../../utils/tableSort';
import { UserName } from '../InactiveBadge';
import OrderAttachmentMarker from '../OrderAttachmentMarker';
import OrderDeliveryPaperworkMarker from '../OrderDeliveryPaperworkMarker';
import useColumnWidths from '../../hooks/useColumnWidths';
import useStickyToolbar from '../../hooks/useStickyToolbar';
import { useVatMode } from '../../hooks/useVatMode';
import { HIRE_STATUS_BADGES } from '../../constants/colors';
import { formatDateCompact, formatDateDayMonth } from '../../utils/dateFormat';
import { compactPersonName } from '../OrderTableView';
import useSwipeTabs from '../../hooks/useSwipeTabs';
import { getOrderNumber } from '../../utils/orderTitle';
import { moneyOrAbsent } from '../../utils/moneyOrAbsent';
import { hireOverdueSignalLabel } from '../../utils/hireOverdueSignal';
import { getHireUrgencyBand } from '../../utils/hireUrgency';
import { HIRE_TABLE_TABS, isHireOverdue, isHireOrderCancelled } from '../../utils/hireBuckets';
import {
  OVERDUE_TRIANGLE_PATH,
  getDaysInfo,
  getRateDisplay,
} from './HireCard';

/**
 * The hires TABLE view — Department Hires and My Hires, beside Kanban / Gantt /
 * Calendar. Owner, 2026-09-27: *"Dep hires and my hires should also have a
 * table view based on the Dep orders/my Orders. The tabs should be: All,
 * Upcomming, On hire, Ending soon, Off hire … By using the orders page code
 * where you can"*.
 *
 * ── What is REUSED, not copied ──────────────────────────────────────────
 * - the table: `ui/PageScrollTable` (extracted from `OrderTableShell` for
 *   this) — pinned header on the PAGE scroll, sticky scrollbar, drag-to-pan,
 *   sortable + resizable headers;
 * - the tabs: `ui/Tabs`, counts included (a `0` renders);
 * - which tab a hire is in: `utils/hireBuckets.js`, the SAME classification
 *   the Kanban columns read — **On hire ⊇ Ending soon ⊇ overdue**, overdue
 *   first wherever it appears (owner ruling 2026-09-27);
 * - 🔀 `Cancelled` (2026-09-28, owner: *"maybe we should have a canceld/reject
 *   tab? like dept orders?"*) — a hire whose ORDER is cancelled or rejected,
 *   last tab, included in `All` (Dept Orders' own shape). It shows in NO
 *   other tab and offers no hire action (`HireOrderExpansion`'s gate);
 * - every cell's wording/colour: `HireCard`'s own `getDaysInfo` /
 *   `getRateDisplay` / overdue triangle, `getHireUrgencyBand`,
 *   `hireOverdueSignalLabel`, `HIRE_STATUS_BADGES`;
 * - 🔻 NO ACTIONS COLUMN since 2026-09-28 (owner: *"I dont think we should
 *   have an action menu until you expand the hire. This would keep
 *   consistancy with orders AND save a whole column"*). Extend / Off hire /
 *   Bring back live in the EXPANDED hire (`HireOrderExpansion`'s
 *   `hireActions`: each line's item menu, and `Off hire all` in the order's
 *   `Actions ▾`); its "View order" link went with it. Pre-deletion SHA
 *   `959f868a`. 🔀 2026-10-03 (owner): the order number is NOT a link
 *   either — tapping the PO/Order cell expands the hire; the way to the
 *   order is the expanded order's Actions ▾ › View in orders
 *   (`hireOrderMenu.js`, the page's `onViewOrder`).
 *   A stored `actions` width self-heals: `useColumnWidths`' `sanitise` keeps
 *   only the keys in `columns`.
 *
 * 💷 Money is `expected_cost` / `current_cost` AS THE SERVER SENT THEM (the
 * figures the card shows) through `moneyOrAbsent`, never re-derived here.
 *
 * ── Columns ────────────────────────────────────────────────────────────
 * No Sub-department column: `GET /api/hires` does not send one, and adding a
 * server field was out of scope (2026-09-27). `Requested by` is the
 * department page's only (`showRequestedBy`) — on My Hires every row is the
 * viewer's — so My Hires is a SUBSET column set with its OWN width key (root
 * `CLAUDE.md`: a subset column set sharing a key corrupts the other's widths).
 *
 * 🔀 **REARRANGED 2026-09-28 to match Dept Orders (owner: *"Dept Hires table
 * needs the columns arranged a little more like Dept orders. Order number
 * should be first and it needs the PO number sort feature when we click on
 * the order number like dept orders"*).** `order` (key `order_number`
 * sortField) is now `po_number` — the SAME cycling PO → Order header and
 * stacked PO-over-order-number cell as `OrderTableView.js`'s `po_number`
 * column, reusing its `fields` shape and its blank-PO-ties-on-order-number
 * rule (`sortHireRows` below), never a second copy. It leads the data
 * columns, ahead of Requested by / Supplier / Item, which is also now the
 * order the columns hand-read left to right on Dept Orders' own table.
 * `key` CHANGED (`order` → `po_number`) — width storage is keyed by
 * column key, so any stored `order` width simply falls back to the default;
 * nobody had customised it (this column shipped less than a day earlier).
 *
 * ── A row EXPANDS INTO ITS ORDER (2026-09-27) ──────────────────────────
 * Owner: *"it should be fairly simple to use the exact same expansion as all
 * of the edit/access rights should work as Hires are just orders"*. With an
 * `expansion` prop, a click anywhere on a row toggles the SAME expanded order
 * panel the order tables render (`components/TableRowExpansion.js` — the
 * chevron, the frame and the panel `<tr>` are the order table's own, shared,
 * never copied). THIS FILE DECIDES NOTHING about the order: `expansion` comes
 * from `components/hires/HireOrderExpansion.js`, which runs the order pages'
 * own hooks (`useDeptOrderExpansion` / `useMyOrderExpansion`) — every gate is
 * the server's `detail.can_edit` / `useOrderAuthority`, exactly as on Orders.
 * One hire LINE is one row, so the panel opens under the row that was clicked
 * (`expandedItemId`) while the hooks key the expansion by ORDER.
 * 🔑 The order-number link calls `stopPropagation` — it acts, it never also
 * toggles the row.
 * Without `expansion` the row is inert, exactly as before.
 *
 * ⚠️ jsdom has no layout: pinning, alignment, drag-to-pan and the computed
 * colours are BROWSER checks (recorded in the PR report), not test claims.
 */

export const HIRE_TABLE_COL_WIDTHS_KEY = 'sfx_hire_table_col_widths';
export const MY_HIRE_TABLE_COL_WIDTHS_KEY = 'sfx_my_hire_table_col_widths';
export const MAX_HIRE_COLUMN_WIDTH = 600;

// `min` on EVERY column — `useColumnWidths` floors a column with none at ZERO.
export const HIRE_TABLE_COLUMNS = Object.freeze([
  // The order tables' own chevron column (`ORDER_TABLE_COLUMNS`' `expand`).
  // `fixed: true` (2026-09-28, `Fit columns to screen`): a pure icon/control
  // column the fit never resizes.
  //
  // 🔀 `fitFloor: true` (2026-09-28, owner ruling: *"none text, or known text
  // width columns first"*): PO/Order, Hire from, Hire to, Days, Rate,
  // Expected, Cost to date and Status are all NON-TEXT or KNOWN-WIDTH (a
  // code, dates, a number, money figures, a badge) and pin at
  // their `min` when fitting. Item, Supplier and Requested by are free text
  // and share whatever room `Fit columns to screen` leaves over.
  //
  // 🔀 2026-09-28: `expand`, PO/Order, Requested by, Supplier and Status are
  // BUILT by `components/sharedTableColumns.js`, the same builders
  // `ORDER_TABLE_COLUMNS` uses — so the columns both tables show cannot drift.
  // The ORDER cycle state sorts on `order_number` here (a hire row's id is
  // not its order's). Supplier's `min` became the shared, measured 100 (was
  // 90, below the heading's own 94.13px).
  expandColumn(),
  poOrderColumn({ orderSortField: 'order_number' }),
  // `min` 100 is this page's own, NOT measured against "Requested by".
  requesterColumn({ key: 'requested_by', label: 'Requested by', width: 140, min: 100 }),
  // 🔀 150 → 120 (2026-09-29, owner: ~20% narrower; min stays 100).
  supplierColumn({ width: 120 }),
  { key: 'description', label: 'Item', width: 220, min: 120, sortField: 'description' },
  { key: 'hire_from', label: 'Hire from', width: 100, min: 96, sortField: 'hire_from', fitFloor: true },
  { key: 'hire_to', label: 'Hire to', width: 100, min: 88, sortField: 'hire_to', fitFloor: true },
  { key: 'days', label: 'Days', width: 70, min: 64, sortField: 'hire_days', align: 'right', fitFloor: true },
  { key: 'rate', label: 'Rate', width: 120, min: 90, fitFloor: true },
  { key: 'expected', label: 'Expected', width: 110, min: 100, sortField: 'expected_cost', align: 'right', fitFloor: true },
  { key: 'cost', label: 'Cost to date', width: 120, min: 116, sortField: 'current_cost', align: 'right', fitFloor: true },
  // `sortField: 'hire_status'` (parity follow-up, 2026-09-28) — RANKED, not
  // alphabetical (`HIRE_STATUS_SORT_RANK` below): alphabetical would read
  // off_hired, on_hire, overdue, pending, which puts the two states nobody
  // needs to act on (`off_hired`, `on_hire`) ahead of the urgent one.
  // 🔀 min 130 → 150 (2026-09-29, owner: the smallest width that fits the
  // longest REAL status line). MEASURED in Chromium against the CI build with
  // Inter: the widest line is the off-hired countdown "Returned 08 May 2026"
  // at 131.92px (`text-xs font-medium`; "123 days overdue" 100.54, "Starts in
  // 123 days" 101.15, "12 days overdue" 93.02; widest badge "Upcoming"
  // 75.42) + `px-2` 16 = 147.92px of cell → 150. It truncated at 130.
  statusColumn({ sortField: 'hire_status', width: 170, min: 150 }),
]);
export const MY_HIRE_TABLE_COLUMNS = Object.freeze(
  HIRE_TABLE_COLUMNS.filter((c) => c.key !== 'requested_by')
);

/**
 * ── THE PHONE TABLE (below `md`, 2026-10-01) ─────────────────────────────
 * Owner brief: the Dept Orders / My Orders phone table, for hires — columns
 * PO/Order · Item · Hire dates (start–end, compact) · Requested by (first name
 * + surname initial, full name in `title`; My Hires omits it, as its desktop
 * set does) · Status · Supplier (last, reached by horizontal scroll).
 *
 * 🔑 Built like `OrderTableView.js`'s `buildPhoneOrderColumns`: every column
 * the desktop set has is the DESKTOP column (key, sort field, cycle states,
 * `min`) with only the phone overrides spread over it — never retyped. The one
 * phone-only column, `hire_dates`, folds the desktop's Hire from + Hire to
 * into ONE cell, so it cycles `Start → End` as a SORT (the My Orders Job/Date
 * shape: the heading reads "Hire dates" in both states, the state rides in an
 * sr-only suffix and the tooltip). No `expand` column — the chevron rides in
 * the PO cell, as on the order phone tables; the whole row is the tap target.
 * Nothing is frozen (owner, 2026-09-30, the order phone tables' ruling).
 * Its OWN width keys: a different column set must never share a key.
 * 📏 Widths: measured in Chromium (CI=true build, demo seed) at 360/375/412,
 * light and dark — no heading or cell spills, page width == viewport. Column resize is off below `md` (`ui/PageScrollTable`).
 */
export const HIRE_TABLE_MOBILE_COL_WIDTHS_KEY = 'sfx_hire_table_mobile_col_widths';
export const MY_HIRE_TABLE_MOBILE_COL_WIDTHS_KEY = 'sfx_my_hire_table_mobile_col_widths';
export const HIRE_DATES_END_LABEL = (
  <>Hire dates<span className="sr-only"> (sorted by end date)</span></>
);
const HIRE_DATES_COLUMN = Object.freeze({
  key: 'hire_dates',
  label: 'Hire dates',
  // 📏 The DATA binds, not the heading: "22 Sept – 27 Sept" (Chromium's
  // en-GB short month is "Sept") measured 112.09px at `text-sm` + `px-2`
  // = 128.09 → 130. The heading fitted inside 124.
  width: 130,
  min: 130,
  align: 'left',
  sortField: 'hire_from',
  fields: [
    { sortField: 'hire_from', label: 'Hire dates', sortLabel: 'Start date', description: 'Sorted by the hire start date.', direction: 'asc' },
    { sortField: 'hire_to', label: HIRE_DATES_END_LABEL, sortLabel: 'End date', description: 'Sorted by the hire end date.', direction: 'asc' },
  ],
});
const HIRE_PHONE_OVERRIDES = Object.freeze({
  // 📱 Owner 2026-10-03: the order phone tables run "po, job, item,
  // supplier…" — hires mirror it: PO · dates (the job/date slot) · Item ·
  // (Requested by) · Supplier · Status.
  po_number: PHONE_PO_ORDER_COLUMN,
  hire_dates: null,
  description: { width: 140, min: 96 },
  // 'Requested by' + caret needs 126px of cell (measured: it spilled 14px
  // out of a 112 column at 360) — so 128, the heading floor.
  requested_by: { width: 128, min: 128 },
  supplier: { width: 112, min: 100 },
  // Badge + countdown line. 📏 The widest REAL line, "Returned 25 Sept
  // 2026", measured 124px at `text-xs font-medium` + `px-2` = 140.
  status: { width: 140, min: 112 },
});
const buildPhoneHireColumns = (overrides) => Object.freeze(tightenPhoneColumns(
  Object.keys(overrides).map((key) => (key === 'hire_dates'
    ? HIRE_DATES_COLUMN
    : Object.freeze({ ...HIRE_TABLE_COLUMNS.find((c) => c.key === key), ...overrides[key] })))
));
export const HIRE_MOBILE_TABLE_COLUMNS = buildPhoneHireColumns(HIRE_PHONE_OVERRIDES);
export const MY_HIRE_MOBILE_TABLE_COLUMNS = Object.freeze(
  HIRE_MOBILE_TABLE_COLUMNS.filter((c) => c.key !== 'requested_by')
);

/**
 * Which column set + width key a hires table renders: the PHONE set below
 * `md`, the desktop one otherwise; Requested by only on the department page.
 * One answer read by the table AND by the page that owns the width state.
 */
export function hireTableColumnModel({ phone = false, showRequestedBy = true } = {}) {
  if (phone) {
    return showRequestedBy
      ? { columns: HIRE_MOBILE_TABLE_COLUMNS, storageKey: HIRE_TABLE_MOBILE_COL_WIDTHS_KEY }
      : { columns: MY_HIRE_MOBILE_TABLE_COLUMNS, storageKey: MY_HIRE_TABLE_MOBILE_COL_WIDTHS_KEY };
  }
  return showRequestedBy
    ? { columns: HIRE_TABLE_COLUMNS, storageKey: HIRE_TABLE_COL_WIDTHS_KEY }
    : { columns: MY_HIRE_TABLE_COLUMNS, storageKey: MY_HIRE_TABLE_COL_WIDTHS_KEY };
}

const NUMERIC_SORT = new Set(['hire_days', 'expected_cost', 'current_cost', 'order_number']);

/**
 * THE Status column's sort order (parity follow-up, 2026-09-28) — mirrors
 * `routes/hires.js`'s own `GET /api/hires` `ORDER BY CASE oi.hire_status`
 * (overdue, on_hire, pending, off_hired), which is itself the tab order
 * (`hireBuckets.js#HIRE_TABLE_TABS`: Upcoming=pending, On hire, Ending
 * soon=on_hire, Off hire) with overdue pulled to the front — the shape the
 * owner already ruled on for "what needs attention first". An unranked value
 * (there should be none — `hire_status` is a fixed enum, `hireBuckets.js`)
 * sorts LAST rather than throwing.
 */
export const HIRE_STATUS_SORT_RANK = Object.freeze({
  overdue: 0, on_hire: 1, pending: 2, off_hired: 3,
});

/**
 * Sort a tab's rows by a header, keeping OVERDUE FIRST whatever the column
 * (owner ruling: overdue sorted to the top). `null` field = the tab's own
 * natural order from `hireTableTabRows`, untouched.
 */
/**
 * A hire row's PO tie-break: its ORDER's number, numerically (a hire row's
 * own id is not its order's). Non-numeric or equal → a tie.
 */
const hireOrderNumberTieBreak = (a, b) => {
  const x = Number(a.order_number); const y = Number(b.order_number);
  return Number.isFinite(x) && Number.isFinite(y) && x !== y ? x - y : 0;
};

/**
 * 🔀 2026-09-28: built on `utils/tableSort.js`'s shared comparators — the PO
 * comparator and blanks-last rule the order pages use, with THIS table's
 * blank test (nullish: a PO of `0` is a value here), direction default
 * (`hireSortSign`: anything but `'desc'` ascends) and tie-break key as
 * parameters. Results are pinned byte-for-byte by
 * `hireSort.characterisation.test.js`, captured before the move.
 */
export function sortHireRows(rows, sortField, sortDir) {
  if (!sortField) return rows;
  const sign = hireSortSign(sortDir);
  const value = (r) => r[sortField];
  return [...rows].sort((a, b) => {
    const oa = isHireOverdue(a); const ob = isHireOverdue(b);
    if (oa !== ob) return oa ? -1 : 1;
    // PO: blanks last in both directions, ties break on the order number and
    // FOLLOW the arrow — the order tables' own rule, shared.
    if (sortField === 'po_number') {
      return compareBlanksLastText(a, b, 'po_number', sign, {
        isBlank: isNullishBlank, tieBreak: hireOrderNumberTieBreak,
      });
    }
    // STATUS SORTS BY RANK, NEVER ALPHABETICALLY.
    if (sortField === 'hire_status') {
      return compareRanked(HIRE_STATUS_SORT_RANK[a.hire_status], HIRE_STATUS_SORT_RANK[b.hire_status], sign);
    }
    const va = value(a); const vb = value(b);
    // Absent values sort LAST in both directions — never compared as 0 or ''.
    const na = isNullishBlank(va); const nb = isNullishBlank(vb);
    if (na || nb) return oneBlankLast(na, nb);
    if (NUMERIC_SORT.has(sortField)) {
      const x = Number(va); const y = Number(vb);
      if (Number.isFinite(x) && Number.isFinite(y)) return (x - y) * sign;
    }
    return String(va).localeCompare(String(vb)) * sign;
  });
}

// The expand column's "Expand" header/resize names are `ui/PageScrollTable`'s
// DEFAULTS since 2026-09-28 — the hand-written pair that stood here (added
// after "Resize undefined column" shipped) is deleted.

const TD = 'px-2 py-2 align-middle text-sm text-gray-700 dark:text-gray-300';
const TD_TRUNC = `${TD} truncate`;
const TD_NUM = `${TD} text-right whitespace-nowrap tabular-nums`;
// The overdue row's leading-edge rule (see the row's first cell).
const OVERDUE_RULE = 'shadow-[inset_4px_0_0_0_#dc2626] dark:shadow-[inset_4px_0_0_0_#f87171]';
// A hire on the viewer's OWN sub-department's order (Dept Hires, a
// sub-department lead — owner 2026-10-09: an accent bar, chosen over a green
// chevron because green already means "yours to action"). A neutral SLATE
// rule on the same leading edge; on an overdue row it sits just INSIDE the red
// one (two inset shadows) so neither signal hides the other.
const OWN_SUBDEPT_RULE = 'shadow-[inset_4px_0_0_0_#64748b] dark:shadow-[inset_4px_0_0_0_#94a3b8]';
const OVERDUE_AND_OWN_RULE = 'shadow-[inset_4px_0_0_0_#dc2626,inset_8px_0_0_0_#64748b] dark:shadow-[inset_4px_0_0_0_#f87171,inset_8px_0_0_0_#94a3b8]';
function leadingEdgeRule(overdue, own) {
  if (overdue && own) return OVERDUE_AND_OWN_RULE;
  if (overdue) return OVERDUE_RULE;
  if (own) return OWN_SUBDEPT_RULE;
  return '';
}
/** Is this hire on the viewer's own sub-department's order? (names, case-insensitive) */
function isOwnSubDepartment(item, ownSubDepartment) {
  return !!ownSubDepartment && !!item.sub_department
    && String(item.sub_department).trim().toLowerCase() === String(ownSubDepartment).trim().toLowerCase();
}

function CountdownText({ item, compact = false }) {
  const info = getDaysInfo(item);
  // The card's colour rule exactly: the unified band, `on_hire` days only.
  const band = getHireUrgencyBand(
    item.hire_status === 'on_hire' ? info.daysUntilReturn : undefined,
    item.hire_status
  );
  const pendingUrgent = item.hire_status === 'pending' && info.urgent;
  const cls = band === 'red'
    ? 'text-red-600 dark:text-red-400'
    : (band === 'amber' || pendingUrgent) ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400';
  // 📱 `compact` (phone, owner 2026-10-03: "reduce the Status second row
  // text size") — 11px, tight leading, so a hire row matches an order row.
  const size = compact ? 'text-[11px] leading-tight mt-0.5' : 'text-xs';
  return info.label ? <span className={`block ${size} font-medium truncate ${cls}`}>{info.label}</span> : null;
}


function HireTableRow({
  item, showRequestedBy, formatMoney,
  expansion, colSpan, panelWidth, ownSubDepartment = '',
}) {
  const own = isOwnSubDepartment(item, ownSubDepartment);
  // `opacity-40` while ANOTHER row holds unsaved edits — the shell's
  // `editingRowId`, the same rule Dept Orders' rows read.
  const rowClassName = useEditingRowDim(item.item_id);
  const overdue = isHireOverdue(item);
  const overdueLabel = hireOverdueSignalLabel(item, formatMoney);
  const badge = HIRE_STATUS_BADGES[item.hire_status] || HIRE_STATUS_BADGES.pending;
  const orderNo = getOrderNumber(item);
  const orderCancelled = isHireOrderCancelled(item);
  const expandable = !!expansion;
  const isExpanded = expandable && expansion.expandedItemId === item.item_id;
  const panel = isExpanded ? expansion.renderPanel(item) : null;
  const rowTint = overdue ? 'bg-red-50/60 dark:bg-red-900/20' : '';
  return (
    <>
    <tr
      className={`${isExpanded
        ? `cursor-pointer ${EXPANDED_ROW_FRAME} ${rowTint}`
        : `${expandable ? 'cursor-pointer ' : ''}hover:bg-gray-50 dark:hover:bg-gray-700/30 ${rowTint}`} ${rowClassName}`.trim()}
      data-testid={`hire-row-${item.item_id}`}
      data-overdue={overdue ? 'true' : undefined}
      data-expanded={isExpanded ? 'true' : undefined}
      onClick={expandable ? (e) => expansion.onToggle(item, e) : undefined}
    >
      {/* The expand cell is the row's LEADING edge, so the overdue rule
          (below) lives here since the chevron column arrived. */}
      <td
        className={`${TD} ${leadingEdgeRule(overdue, own)}`}
        data-col="expand"
        data-testid={`hire-row-toggle-${item.item_id}`}
        data-own-sub-department={own ? 'true' : undefined}
        // The bar is otherwise the ONLY visible carrier of "yours" — a hover
        // explains it (design pass 2026-10-09). The chevron in this cell has
        // no `title` of its own, so nothing shadows this one.
        title={own ? `In your sub-department (${ownSubDepartment})` : undefined}
      >
        {own ? <span className="sr-only">{`In your sub-department (${ownSubDepartment})`}</span> : null}
        {expandable ? (
          <ExpandChevron expanded={isExpanded} testId={`hire-row-chevron-${item.item_id}`} />
        ) : null}
      </td>
      {/* 🔴 The overdue flag's GEOMETRY channel: a 4px red rule down the row's
          leading edge (an inset shadow, so it costs no width and survives
          `table-fixed`). Colour is never the only channel — the Status badge
          reads "Overdue" and the cost cell carries the triangle + sr-only
          sentence, the card's device. */}
      {/* PO/Order — the stacked cell `OrderTableView.js`'s `po_number` column
          uses, PO on top (via the shared `PoOrderTopLine`, `''` for none, no
          dash — root CLAUDE.md's PO column bullet), the order number always,
          still the row's ONE click-through to its order. */}
      <td className={TD} data-col="po_number">
        <PoOrderTopLine poNumber={item.po_number} testId={`hire-row-po-value-${item.item_id}`} />
        {orderNo ? (
          // 🔀 2026-10-03 (owner): NOT a link any more — a tap anywhere in
          // the cell expands the hire in place (the row's own onClick), as on
          // the order tables. The way to the order is Actions ▾ › View in
          // orders (`hireOrderMenu.js`). Grey, as the order tables' cell.
          <div
            className="text-xs font-semibold text-gray-500 dark:text-gray-400 truncate leading-tight"
            title={orderNo}
            data-testid={`hire-row-order-${item.item_id}`}
          >
            {orderNo}
            {/* Nested INSIDE the order-number element, same rule
                `OrderTableView.js`'s PO cell follows: each marker carries its
                OWN native `title`, which a parent's `title` would otherwise
                shadow. */}
            <OrderDeliveryPaperworkMarker present={item.has_delivery_paperwork} files={item.delivery_files} className="ml-1" />
            <OrderAttachmentMarker count={item.file_count} files={item.attachment_files} className="ml-1" />
          </div>
        ) : '-'}
      </td>
      {showRequestedBy ? (
        <td className={TD_TRUNC} title={item.requested_by || undefined}>
          <UserName name={item.requested_by} isActive={item.is_active} />
        </td>
      ) : null}
      <td className={TD_TRUNC} title={item.supplier_name || undefined}>{item.supplier_name || '-'}</td>
      <td
        className={`${TD_TRUNC} font-medium text-gray-800 dark:text-gray-100`}
        title={item.description || undefined}
      >
        {item.description || 'Unnamed item'}
      </td>
      <td className={`${TD} whitespace-nowrap`}>{formatDateCompact(item.hire_from)}</td>
      <td className={`${TD} whitespace-nowrap`}>{formatDateCompact(item.hire_to)}</td>
      <td className={TD_NUM}>{item.hire_days > 0 ? item.hire_days : '-'}</td>
      <td className={`${TD_TRUNC} text-gray-500 dark:text-gray-400`}>{getRateDisplay(item, formatMoney)}</td>
      <td className={TD_NUM}>{moneyOrAbsent(item.expected_cost)}</td>
      <td className={TD_NUM}>
        <span
          className={`inline-flex items-center gap-1 font-medium ${
            overdueLabel || item.current_cost > item.expected_cost
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-800 dark:text-gray-200'
          }`}
          title={overdueLabel || undefined}
        >
          {overdueLabel && (
            <svg className="w-4 h-4 inline text-red-600 dark:text-red-400" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={OVERDUE_TRIANGLE_PATH} />
            </svg>
          )}
          {moneyOrAbsent(item.current_cost)}
        </span>
        {overdueLabel && <span className="sr-only">{overdueLabel}</span>}
      </td>
      <td className={TD}>
        <span className={`inline-block ${statusPillClass()} ${badge.bg} ${badge.text}`} data-testid={`hire-row-status-${item.item_id}`}>
          {badge.label}
        </span>
        <CountdownText item={item} />
        {orderCancelled ? (
          <span className="block text-xs text-gray-500 dark:text-gray-400" data-testid={`hire-row-order-cancelled-${item.item_id}`}>
            {item.order_status === 'rejected' ? 'Order rejected' : 'Order cancelled'}
          </span>
        ) : null}
      </td>
    </tr>
    {/* Always rendered: `open` eases it closed too (`hooks/useExpandAnimation.js`). */}
    <ExpandedPanelRow
      open={isExpanded}
      colSpan={colSpan}
      panelWidth={panelWidth}
      testId={`hire-row-details-${item.item_id}`}
      isLoading={panel?.isLoading}
      detail={panel?.detail}
    >
      {panel?.content}
    </ExpandedPanelRow>
    </>
  );
}

/** "1 Sep – 22 Sep": the phone Hire dates cell. A missing date reads "?". */
export function compactHireDates(from, to) {
  const a = from ? formatDateDayMonth(from) : '?';
  const b = to ? formatDateDayMonth(to) : '?';
  return `${a} – ${b}`;
}

/**
 * THE PHONE row (below `md`), against `HIRE_MOBILE_TABLE_COLUMNS` /
 * `MY_HIRE_MOBILE_TABLE_COLUMNS`. Same `item`, `expansion` and testids as
 * `HireTableRow`, so the expanded order (the SAME panel, every gate the
 * order pages' own) is unchanged; the cells are emitted IN THE COLUMN ARRAY'S
 * ORDER by key, `OrderMobileTableRow`'s rule, so a set can never put a cell
 * under the wrong heading. `h-11` on a cell = the 44px tap-target floor.
 */
function HireMobileTableRow({ item, columns, expansion, panelWidth, ownSubDepartment = '' }) {
  const own = isOwnSubDepartment(item, ownSubDepartment);
  const rowClassName = useEditingRowDim(item.item_id);
  const overdue = isHireOverdue(item);
  const badge = HIRE_STATUS_BADGES[item.hire_status] || HIRE_STATUS_BADGES.pending;
  const orderNo = getOrderNumber(item);
  const orderCancelled = isHireOrderCancelled(item);
  const expandable = !!expansion;
  const isExpanded = expandable && expansion.expandedItemId === item.item_id;
  const panel = isExpanded ? expansion.renderPanel(item) : null;
  const rowTint = overdue ? 'bg-red-50/60 dark:bg-red-900/20' : '';
  const TDM_TEXT = 'h-11 py-1 align-middle text-sm text-gray-700 dark:text-gray-300';
  const TDM = `px-2 ${TDM_TEXT}`;
  const mutedDash = <span className="text-gray-400 dark:text-gray-500">-</span>;
  const datesTitle = `${formatDateCompact(item.hire_from)} – ${item.hire_to ? formatDateCompact(item.hire_to) : 'no end date'}`;

  const cells = {
    /* PO / Order + the chevron (no expand column on a phone). The overdue
       rule rides this cell — it is the row's leading edge here. */
    po_number: (
      <PhonePoOrderCell
        key="po_number"
        className={leadingEdgeRule(overdue, own)}
        // The phone row's own-sub-department explanation (title + sr-only) —
        // the bar alone is colour only on a touch screen (review M4).
        chevronState={own ? `In your sub-department (${ownSubDepartment})` : undefined}
        testId={`hire-row-toggle-${item.item_id}`}
        showChevron={expandable}
        expanded={isExpanded}
        chevronTestId={`hire-row-chevron-${item.item_id}`}
        poNumber={item.po_number}
        poTestId={`hire-row-po-value-${item.item_id}`}
        orderNumber={orderNo}
        hasDeliveryPaperwork={item.has_delivery_paperwork}
        deliveryFiles={item.delivery_files}
        fileCount={item.file_count}
        attachmentFiles={item.attachment_files}
        orderTestId={`hire-row-order-${item.item_id}`}
      />
    ),
    description: (
      <td
        key="description"
        data-col="description"
        className={`${TDM} truncate font-medium text-gray-800 dark:text-gray-100`}
        title={item.description || undefined}
      >
        {item.description || 'Unnamed item'}
      </td>
    ),
    hire_dates: (
      <td key="hire_dates" data-col="hire_dates" className={`${TDM} whitespace-nowrap truncate`} title={datesTitle} data-testid={`hire-row-dates-${item.item_id}`}>
        {compactHireDates(item.hire_from, item.hire_to)}
      </td>
    ),
    requested_by: (
      <td key="requested_by" data-col="requested_by" className={`${TDM} truncate`} title={item.requested_by || undefined}>
        {item.requested_by
          ? <UserName name={compactPersonName(item.requested_by)} isActive={item.is_active} />
          : mutedDash}
      </td>
    ),
    status: (
      <td key="status" data-col="status" className={TDM}>
        <span className={`inline-block max-w-full truncate leading-tight ${statusPillClass()} ${badge.bg} ${badge.text}`} data-testid={`hire-row-status-${item.item_id}`}>
          {badge.label}
        </span>
        <CountdownText item={item} compact />
        {orderCancelled ? (
          <span className="block truncate text-[11px] leading-tight mt-0.5 text-gray-500 dark:text-gray-400" data-testid={`hire-row-order-cancelled-${item.item_id}`}>
            {item.order_status === 'rejected' ? 'Order rejected' : 'Order cancelled'}
          </span>
        ) : null}
      </td>
    ),
    supplier: (
      <td key="supplier" data-col="supplier" className={`${TDM} truncate`} title={item.supplier_name || undefined}>
        {item.supplier_name || mutedDash}
      </td>
    ),
  };

  return (
    <>
      <tr
        className={`${isExpanded
          ? `cursor-pointer ${EXPANDED_ROW_FRAME} ${rowTint}`
          : `${expandable ? 'cursor-pointer ' : ''}hover:bg-gray-50 dark:hover:bg-gray-700/30 ${rowTint}`} select-none ${rowClassName}`.trim()}
        data-testid={`hire-row-${item.item_id}`}
        data-mobile-row=""
        data-overdue={overdue ? 'true' : undefined}
        data-expanded={isExpanded ? 'true' : undefined}
        onClick={expandable ? (e) => expansion.onToggle(item, e) : undefined}
      >
        {columns.map((c) => cells[c.key] || null)}
      </tr>
      {/* Always rendered: `open` eases it closed too (`hooks/useExpandAnimation.js`). */}
      <ExpandedPanelRow
          open={isExpanded}
          colSpan={columns.length}
          panelWidth={panelWidth}
          testId={`hire-row-details-${item.item_id}`}
          isLoading={panel?.isLoading}
          detail={panel?.detail}
        >
          {/* 📱 one type step smaller below md — index.css, as the order rows */}
          {/* 📱 The Order Details header becomes the row's extra line, as on the
              order rows; no Due note — the row already shows the hire dates. */}
          <OrderHeaderNoteContext.Provider value={{ note: null,
            // A tap on the row's extension line closes it, as on the order rows (2026-10-10).
            onRowTap: expandable ? () => expansion.onToggle(item) : null }}>
            <div data-mobile-compact="">{panel?.content}</div>
          </OrderHeaderNoteContext.Provider>
        </ExpandedPanelRow>
    </>
  );
}

/**
 * The Table's status tabs (All · Upcoming · On hire · Ending soon · Off hire),
 * each with its count — rendered by the PAGE in its chrome, driven by the
 * `list` from `hooks/useHireTableList.js`. `bare` when it sits inside a bar.
 */
export function HireStatusTabs({ list, onTabChange = null, bare = false, className, phone = false }) {
  return (
    <Tabs
      scrollHints
      tabs={list.tabs}
      active={list.tab}
      onChange={onTabChange || list.setTab}
      idPrefix="hire-table"
      ariaLabel="Hire status"
      bare={bare}
      className={className}
      /* 📱 Below `md`: snap-to-centre + flush start beside the magnifier —
         the order phone tables' strip (`ui/Tabs`' docblock). PHONE ONLY:
         `snapCenter` also changes the strip's classes at desktop widths. */
      snapCenter={phone}
      snapFlushStart={phone}
    />
  );
}

export default function HireTableView({
  showRequestedBy = true,
  // The viewer's own sub-department NAME on a department-wide list (from
  // `HiresPage`), '' otherwise — rows on it get the slate accent bar.
  ownSubDepartment = '',
  // `hooks/useHireTableList.js`'s return: the tab, the sorted tab rows, the
  // page slice (dirty row pinned), the sort and the pager. The table renders
  // `list.pageRows` and decides nothing about which rows those are.
  list,
  // The pager's measured sticky offset comes from the PAGE's toolbar hook.
  stickyTop,
  // `{ expandedItemId, onToggle(item, event), renderPanel(item) → { isLoading,
  // detail, content } }` — see the docblock. Absent: rows do not expand.
  expansion = null,
  /**
   * 🔀 LIFTED WIDTH STATE, added 2026-09-28 for `Fit columns to screen`.
   * **All three optional, and all three or none** — a page building an
   * `Actions ▾` menu with a Fit item needs the SAME width map the table
   * renders from (one write path, never two routes into one piece of
   * state — the Breakdown's own rule for why the page owns the hook, not
   * the table). Omit them and this component calls `useColumnWidths`
   * itself exactly as it always has, which is what keeps
   * `HireTableView.test.js`'s bare-mount cases unchanged.
   */
  columnWidths = null,
  onColumnResize = null,
  onColumnResetWidth = null,
  // Forwarded to `PageScrollTable`'s own prop — see its docblock.
  onRegionWidthChange = null,
  /**
   * The hire line whose expanded ORDER FORM holds unsaved edits (parity
   * follow-up, 2026-09-28) — handed to `ui/PageScrollTable`'s `editingRowId`,
   * the ONE dimming rule Dept Orders uses too. Every OTHER row dims; nothing is
   * `pointer-events-none` (owner, 2026-09-28: a click on another row must
   * still reach the ask-then-continue guard, not do nothing). `null` (the
   * default): no row dims. Reported up by `HireOrderExpansion`'s own
   * `onDirtyItemChange` — the page threads the SAME id back down here.
   */
  dirtyItemId = null,
  /**
   * 📱 Below `md` (2026-10-01): the PHONE column set and row
   * (`hireTableColumnModel`), and a swipe on the list changes the status tab
   * (`hooks/useSwipeTabs.js`, the order phone tables' rules). The PAGE decides
   * (`useIsMobileOrderViewport()`), so the page's lifted width map and this
   * table always agree on which set they mean. `false`: desktop, unchanged.
   */
  phone = false,
  // The guarded tab change a swipe calls (the page's `continueWhenClean`
  // around `list.setTab`, the same one the status tabs call). Absent:
  // `list.setTab`.
  onSwipeTab = null,
}) {
  const { formatMoney } = useVatMode();
  const { columns, storageKey } = hireTableColumnModel({ phone, showRequestedBy });
  // Always called (Hooks rules), never both routes wired to storage at
  // once in practice: a page passing its own map calls `useColumnWidths`
  // ONCE, up in itself, and hands the result down — this internal call
  // then simply has no caller and its own `hydrated` guard keeps it from
  // ever writing.
  const internalWidths = useColumnWidths(columns, storageKey, MAX_HIRE_COLUMN_WIDTH);
  const widths = columnWidths || internalWidths.widths;
  const setWidth = onColumnResize || internalWidths.setWidth;
  const resetColumn = onColumnResetWidth || internalWidths.resetColumn;
  // 🔀 The pages have a sticky `ui/Toolbar` since 2026-09-27, so the column
  // header pins beneath IT: the page passes its own `useStickyToolbar()`'s
  // measured `headerTop` as `stickyTop`. Without one (a bare table), this
  // hook's own `headerTop` is the app bar alone. Measured, never a constant.
  const { headerTop: appBarTop } = useStickyToolbar();
  const headerTop = stickyTop != null ? stickyTop : appBarTop;
  const { tab, rows, pageRows, sortField, sortDir, onHeaderSort } = list;
  const swipeAreaRef = useRef(null);
  const swipeHandlers = useSwipeTabs(swipeAreaRef, {
    tabs: list.tabs || [],
    active: tab,
    onChange: onSwipeTab || list.setTab,
    enabled: phone,
  });

  return (
    <div
      data-testid="hire-table"
      {...(phone ? { ref: swipeAreaRef, ...swipeHandlers, 'data-swipe-area': '' } : {})}
    >
      {rows.length === 0 && pageRows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic py-8 text-center" data-testid="hire-table-empty">
          No {HIRE_TABLE_TABS.find((t) => t.id === tab).label.toLowerCase()} hires
        </p>
      ) : (
        <PageScrollTable
          phoneFullBleed
          testIdPrefix="hire"
          columns={columns}
          sortField={sortField}
          sortDir={sortDir}
          onHeaderSort={onHeaderSort}
          columnWidths={widths}
          onColumnResize={setWidth}
          onColumnResetWidth={resetColumn}
          stickyTop={headerTop}
          maxColumnWidth={MAX_HIRE_COLUMN_WIDTH}
          resizeTestIdPrefix="hire-col-resize"
          onRegionWidthChange={onRegionWidthChange}
          // THE shared dirty-row dimming (`useEditingRowDim` in the row).
          editingRowId={dirtyItemId}
          // The rows are built HERE, not passed as children, because the
          // expanded panel needs the shell's measured `panelWidth` — which is
          // what `wrapRows` hands over (the order table gets it the same way).
          wrapRows={(_children, panelWidth) => pageRows.map((item) => (phone ? (
            <HireMobileTableRow
              key={item.item_id}
              item={item}
              columns={columns}
              expansion={expansion}
              panelWidth={panelWidth}
              ownSubDepartment={ownSubDepartment}
            />
          ) : (
            <HireTableRow
              key={item.item_id}
              item={item}
              showRequestedBy={showRequestedBy}
              formatMoney={formatMoney}
              expansion={expansion}
              colSpan={columns.length}
              panelWidth={panelWidth}
              ownSubDepartment={ownSubDepartment}
            />
          )))}
        />
      )}
      {/* The order pages' pager, verbatim in shape: shown only when the tab
          has rows; `totalItems` is the tab's rows, never the pinned extra. */}
      {rows.length > 0 && (
        <div className="mt-4">
          <Pagination
            currentPage={list.currentPage}
            onPageChange={list.setCurrentPage}
            pageSize={list.pageSize}
            onPageSizeChange={list.onPageSizeChange}
            totalItems={rows.length}
          />
        </div>
      )}
    </div>
  );
}
